import React, { useState, useEffect, useContext } from 'react';
import { DataContext } from '../DataContexts/DataContext';
import './TagManager.css';

// DJB-100: the old standalone Tags sub-page, redesigned as a section that
// lives below the Upload section on the Library > Upload page. Create-tag UI
// sits on top, the search tool is integrated in the first (list) column and
// the second column holds the selected tag's details/editor.
const TagManager = () => {
    // This admin section is the one tag view that needs count + classifier, so
    // it reads the ExtendedTagDTO list (/tags/extended) instead of plain tags
    const { loading, error, extendedTags, updateTag, createTag, deleteTag } = useContext(DataContext);
    const [visibleTags, setVisibleTags] = useState([]);
    const [page, setPage] = useState(1);
    const [selectedTag, setSelectedTag] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedGenre, setSelectedGenre] = useState('All');
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editGenre, setEditGenre] = useState('');
    const [newTagName, setNewTagName] = useState('');
    const [newTagGenre, setNewTagGenre] = useState('');
    const [showEditDropdown, setShowEditDropdown] = useState(false);
    const [showCreateDropdown, setShowCreateDropdown] = useState(false);
    const ITEMS_PER_PAGE = 100;

    const [filteredTags, setFilteredTags] = useState([]);

    useEffect(() => {
        setFilteredTags(
            extendedTags
                .filter((tag) =>
                    tag.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
                    (selectedGenre === 'All' || tag.classifier === selectedGenre)
                )
                .sort((a, b) => a.name.localeCompare(b.name))
        );
        setPage(1); // Reset pagination when filters change
    }, [extendedTags, searchQuery, selectedGenre]);

    useEffect(() => {
        setVisibleTags(filteredTags.slice(0, page * ITEMS_PER_PAGE));
    }, [page, filteredTags]);

    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
        if (bottom && visibleTags.length < filteredTags.length) {
            setPage((prevPage) => prevPage + 1);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handleGenreChange = (e) => {
        setSelectedGenre(e.target.value);
        setPage(1);
    };

    const handleCreateTag = async () => {
        if (!newTagName.trim() || !newTagGenre.trim()) {
            alert('Please provide both a name and type for the tag.');
            return;
        }

        const newTag = { name: newTagName.trim(), classifier: newTagGenre.trim() };

        try {
            await createTag(newTag);
            setNewTagName('');
            setNewTagGenre('');
        } catch (error) {
            console.error('Failed to create tag:', error);
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
        setEditName(selectedTag.name);
        setEditGenre(selectedTag.classifier);
    };

    const handleSaveClick = async () => {
        try {
            await updateTag(selectedTag.id, { name: editName, classifier: editGenre });
            setIsEditing(false);
            setSelectedTag({ ...selectedTag, name: editName, classifier: editGenre });
        } catch (error) {
            console.error('Failed to update tag:', error);
        }
    };

    const handleCancelClick = () => {
        setIsEditing(false);
        setEditName(selectedTag.name);
        setEditGenre(selectedTag.classifier);
    };

    const handleDeleteClick = async () => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete the tag "${selectedTag.name}"?`
        );
        if (confirmDelete) {
            try {
                await deleteTag(selectedTag.id);
                setSelectedTag(null);
            } catch (error) {
                console.error('Failed to delete tag:', error);
            }
        }
    };

    const renderAutosuggest = (inputValue, setInputValue, setDropdownVisibility, isDropdownVisible, placeholder) => {
        // classifier is NON_NULL on the wire, so filter out tags that omit it
        const uniqueTagTypes = [...new Set(extendedTags.map((tag) => tag.classifier))].filter(Boolean);

        const matchingTypes = uniqueTagTypes.filter((type) =>
            type.toLowerCase().includes(inputValue.toLowerCase())
        );

        return (
            <div className="tag-dropdown">
                <input
                    type="text"
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onFocus={() => setDropdownVisibility(true)}
                    onBlur={() => setDropdownVisibility(false)}
                    className="tag-field"
                />
                {isDropdownVisible && matchingTypes.length > 0 && (
                    <div className="dropdown-list">
                        {matchingTypes.map((type, index) => (
                            <div
                                key={index}
                                className="dropdown-item"
                                onMouseDown={() => {
                                    setInputValue(type);
                                    setDropdownVisibility(false);
                                }}
                            >
                                {type}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
      return (
        <div>
          <p>Loading data, please wait... 🎵</p>
        </div>
      );
    }

    if (error) {
      return (
        <div>
          <h1>Tags</h1>
          <p>Error loading data: {error}</p>
        </div>
      );
    }

    return (
        // The "Tags" title, the section-tags anchor and the collapsible shell
        // live on the DashboardSection wrapper in LibraryHeader (DJB-159)
        <div className="tag-section">
            {/* Create-tag UI on top of the section */}
            <div className="tag-create-bar">
                <span className="tag-create-label">New tag</span>
                <input
                    id="newTagName"
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    className="tag-field"
                    placeholder="Tag name"
                />
                {renderAutosuggest(newTagGenre, setNewTagGenre, setShowCreateDropdown, showCreateDropdown, 'Type or select a type')}
                <button className="tag-btn tag-btn--primary" onClick={handleCreateTag}>
                    Create tag
                </button>
            </div>

            <div className="tag-columns">
                {/* First column: search tool + tag list */}
                <div className="tag-column tag-column--list">
                    <div className="tag-list-tools">
                        <input
                            type="text"
                            placeholder="Search tags..."
                            className="tag-field tag-search-box"
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                        <select
                            className="tag-field tag-genre-dropdown"
                            value={selectedGenre}
                            onChange={handleGenreChange}
                        >
                            <option value="All">All</option>
                            {[...new Set(extendedTags.map((tag) => tag.classifier))].filter(Boolean).map((genre) => (
                                <option key={genre} value={genre}>
                                    {genre}
                                </option>
                            ))}
                        </select>
                        <span className="tag-section-count">{filteredTags.length}</span>
                    </div>
                    <ul className="tag-list-content" onScroll={handleScroll}>
                        {visibleTags.map((tag) => (
                            <li
                                key={tag.id}
                                className={selectedTag?.id === tag.id ? 'selected' : ''}
                                onClick={() => setSelectedTag(tag)}
                            >
                                {/* DJB-199: tag ID below the name, track count in a pill on the right */}
                                <div className="tag-row-main">
                                    <span className="tag-name">{tag.name}</span>
                                    <span className="tag-id">{tag.id}</span>
                                </div>
                                <span className="tag-type">{tag.classifier}</span>
                                <span className="tag-track-count">{tag.count ?? 0}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Second column: selected tag details / editor */}
                <div className="tag-column tag-column--detail">
                    {selectedTag ? (
                        !isEditing ? (
                            // DJB-199: Edit pinned top-right, Delete bottom-right;
                            // ID and track count listed below name and type
                            <>
                                <button className="tag-btn tag-btn--primary tag-detail-edit-btn" onClick={handleEditClick}>
                                    Edit
                                </button>
                                <p><strong>Name:</strong> {selectedTag.name}</p>
                                <p><strong>Type:</strong> {selectedTag.classifier}</p>
                                <p><strong>ID:</strong> {selectedTag.id}</p>
                                <p><strong>Tracks:</strong> {selectedTag.count ?? 0}</p>
                                <button className="tag-btn tag-btn--danger tag-detail-delete-btn" onClick={handleDeleteClick}>
                                    Delete
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="tag-edit-field">
                                    <label htmlFor="editName"><strong>Name:</strong></label>
                                    <input
                                        id="editName"
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        className="tag-field"
                                    />
                                </div>
                                <div className="tag-edit-field">
                                    <label><strong>Type:</strong></label>
                                    {renderAutosuggest(editGenre, setEditGenre, setShowEditDropdown, showEditDropdown, 'Type or select a type')}
                                </div>
                                <div className="tag-button-row">
                                    <button className="tag-btn tag-btn--primary" onClick={handleSaveClick}>
                                        Save
                                    </button>
                                    <button className="tag-btn tag-btn--danger" onClick={handleCancelClick}>
                                        Cancel
                                    </button>
                                </div>
                            </>
                        )
                    ) : (
                        <p className="tag-detail-empty">Select a tag to view or edit it.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TagManager;
